import React, { useContext, useEffect, useRef, useState } from "react";
import { Avatar, IconButton, Input, Paper } from "@mui/material";
import { makeStyles } from 'tss-react/mui';
import SendIcon from "@mui/icons-material/Send";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";
import whatsBackground from "../../assets/wa-background.png";

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const useStyles = makeStyles()((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflow: "hidden",
    borderRadius: 0,
    height: "100%",
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 16px",
    backgroundColor: theme.palette.mode === "light" ? "#f0f2f5" : "#202c33",
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  headerAvatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: 15,
    fontWeight: 600,
    width: 40,
    height: 40,
  },
  headerInfo: {
    minWidth: 0,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 15,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 420,
  },
  messageList: {
    position: "relative",
    overflowY: "auto",
    flex: 1,
    padding: "10px 14px",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.mode === "light" ? "#efeae2" : "#0b141a",
    backgroundImage:
      theme.palette.mode === "light"
        ? `url(${whatsBackground})`
        : `linear-gradient(rgba(11,20,26,0.86), rgba(11,20,26,0.86)), url(${whatsBackground})`,
  },
  messageRow: {
    display: "flex",
    marginBottom: 6,
  },
  messageRowRight: {
    justifyContent: "flex-end",
  },
  bubble: {
    padding: "6px 9px 6px",
    maxWidth: "70%",
    minWidth: 90,
    borderRadius: 8,
    fontSize: 14,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    boxShadow: theme.palette.mode === "light" ? "0 1px 0.5px rgba(0,0,0,0.13)" : "none",
  },
  bubbleLeft: {
    backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#202c33",
    color: theme.palette.text.primary,
    borderTopLeftRadius: 0,
  },
  bubbleRight: {
    backgroundColor: theme.palette.mode === "light" ? "#d9fdd3" : "#005c4b",
    color: theme.palette.mode === "light" ? "#111b21" : "#e9edef",
    borderTopRightRadius: 0,
  },
  senderName: {
    fontWeight: 600,
    fontSize: 12.5,
    marginBottom: 2,
    color: theme.palette.primary.main,
  },
  timestamp: {
    display: "block",
    textAlign: "right",
    fontSize: 10.5,
    marginTop: 2,
    opacity: 0.6,
  },
  inputArea: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    padding: 10,
    backgroundColor: theme.palette.mode === "light" ? "#f0f2f5" : "#202c33",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  inputWrapper: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 20,
    padding: "8px 14px",
  },
  input: {
    fontSize: 14,
  },
  buttonSend: {
    color: theme.palette.primary.main,
  },
}));

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading,
}) {
  const { classes, cx } = useStyles();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const baseRef = useRef();

  const [contentMessage, setContentMessage] = useState("");

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({});
    }
  };

  const unreadMessages = (chat) => {
    if (chat !== undefined) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      return currentUser.unreads > 0;
    }
    return 0;
  };

  useEffect(() => {
    if (unreadMessages(chat) > 0) {
      try {
        api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }
    scrollToBottomRef.current = scrollToBottom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo.hasMore || loading) return;
    if (scrollTop < 600) {
      handleLoadMore();
    }
  };

  const submit = () => {
    if (contentMessage.trim() !== "") {
      handleSendMessage(contentMessage);
      setContentMessage("");
    }
  };

  return (
    <Paper className={classes.mainContainer}>
      {chat && (
        <div className={classes.header}>
          <Avatar className={classes.headerAvatar}>
            {getInitials(chat.title)}
          </Avatar>
          <div className={classes.headerInfo}>
            <div className={classes.headerTitle}>{chat.title}</div>
            <div className={classes.headerSubtitle}>
              {(chat.users || []).map((u) => u.user?.name).filter(Boolean).join(", ")}
            </div>
          </div>
        </div>
      )}
      <div onScroll={handleScroll} className={classes.messageList}>
        {Array.isArray(messages) &&
          messages.map((item, key) => {
            const isMine = item.senderId === user.id;
            return (
              <div
                key={key}
                className={cx(classes.messageRow, {
                  [classes.messageRowRight]: isMine,
                })}
              >
                <div
                  className={cx(
                    classes.bubble,
                    isMine ? classes.bubbleRight : classes.bubbleLeft
                  )}
                >
                  {!isMine && (
                    <div className={classes.senderName}>{item.sender.name}</div>
                  )}
                  {item.message}
                  <span className={classes.timestamp}>
                    {datetimeToClient(item.createdAt)}
                  </span>
                </div>
              </div>
            );
          })}
        <div ref={baseRef}></div>
      </div>
      <div className={classes.inputArea}>
        <div className={classes.inputWrapper}>
          <Input
            disableUnderline
            fullWidth
            multiline
            maxRows={4}
            placeholder="Escribe un mensaje"
            value={contentMessage}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            onChange={(e) => setContentMessage(e.target.value)}
            className={classes.input}
          />
        </div>
        <IconButton
          onClick={submit}
          disabled={contentMessage.trim() === ""}
          className={classes.buttonSend}
          size="large"
        >
          <SendIcon />
        </IconButton>
      </div>
    </Paper>
  );
}
